import React, { useState } from "react";
import { Form, Formik } from "formik";
import { Input } from "../ui/input";
import CustomLabelText from "../CustomLabelText";
import { Label } from "../ui/label";
import EmailChipInput from "./EmailChipInput";
import { Button } from "../ui/button";
import { Download, LucidePlusCircle, X } from "lucide-react";
import { Badge } from "../ui/badge";
import { toast } from "../ui/use-toast";
// import EmailChipsInput from "./EmailChipsInput";

const SendEmailAttach = ({
  handleSubmit,
  setFiles,
  files,
  toggleCancel,
  mailData,
  type,
}) => {
  // Handle file addition
  const handleAddFiles = (event) => {
    const files = Array.from(event.target.files);
    const invalidFiles = files.filter(
      (file) => !allowedFileTypes.includes(file.type),
    );

    if (invalidFiles.length > 0) {
      toast({
        variant: "destructive",
        description: `Invalid file type(s): ${invalidFiles
          .map((file) => file.name)
          .join(
            ", ",
          )}. Please upload PDF, images, Word, Excel, or CSV files only.`,
        duration: 3000,
      });
    } else {
      setFiles((prev) => [...prev, ...files]);
    }
  };

  // Handle file removal
  const handleRemoveFile = (fileIndex) => {
    setFiles((prevAttachments) =>
      prevAttachments.filter((_, index) => index !== fileIndex),
    );
  };

  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);

  const allowedFileTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "text/csv", // .csv
  ];

  const handleDownload = async (export_report_path) => {
    try {
      if (export_report_path) {
        var a = document.createElement("a");
        a.href = export_report_path;
        var file = export_report_path.split("/");
        a.setAttribute("download", file[file.length - 1] || "file.pdf");
        a.setAttribute("target", "_blank");
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e) {
      const errors = e.response?.data?.errors;
      toast({
        variant: "destructive",
        description: errors,
        duration: 3000,
      });
    }
  };

  return (
    <Formik
      initialValues={{
        to: mailData?.email ? [mailData?.email] : [],
        cc: [],
        bcc: [],
        subject: mailData?.subject ? mailData?.subject : "",
      }}
      //   validationSchema={sendEmailValidationSchema}
      onSubmit={(values) => handleSubmit(values)}
    >
      {({
        setFieldValue,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
      }) => (
        <Form className="entry-section email-form">
          <div className="grid p-1 gap-3">
            <EmailChipInput
              label="To"
              value={values.to}
              required={true}
              setValue={(emails) => setFieldValue("to", emails)}
              errors={touched.to && errors.to ? errors.to : ""}
            />

            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                className="p-2 h-5"
                onClick={() => setShowCC(!showCC)}
              >
                {showCC ? "Hide CC" : "Add CC"}
              </Button>
              <Button
                type="button"
                size="sm"
                className="p-2 h-5"
                onClick={() => setShowBCC(!showBCC)}
              >
                {showBCC ? "Hide BCC" : "Add BCC"}
              </Button>
            </div>

            {showCC && (
              <EmailChipInput
                label="CC"
                value={values.cc}
                setValue={(emails) => setFieldValue("cc", emails)}
                errors={touched.cc && errors.cc ? errors.cc : ""}
              />
            )}
            {showBCC && (
              <EmailChipInput
                label="BCC"
                value={values.bcc}
                setValue={(emails) => setFieldValue("bcc", emails)}
                errors={touched.bcc && errors.bcc ? errors.bcc : ""}
              />
            )}
            <div className="grid gap-2">
              <Label htmlFor="subject">
                <CustomLabelText
                  required={true}
                  labelText="Subject"
                  errorField={errors?.subject}
                />
              </Label>
              <Input
                id="subject"
                placeholder="Enter Subject"
                name="subject"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values?.subject}
                className={
                  errors?.subject && `focus-visible:ring-red-500 border-red-500`
                }
              />
            </div>

            <div className="form-group full-width">
              <div className="attachment-button">
                <input
                  type="file"
                  className="form-control"
                  multiple
                  onChange={handleAddFiles}
                  accept=".pdf, image/*, .xls, .xlsx, .doc, .docx, .csv"
                  style={{ display: "none" }}
                  id="file-input"
                />
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("file-input").click();
                  }}
                  className="p-2 h-7"
                >
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} /> <span>Attachment</span>
                  </div>
                </Button>
              </div>
              {(files.length > 0 || mailData?.receipt_file) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {/* Existing Mail File */}
                  {mailData?.receipt_file?.length > 0 &&
                    mailData?.receipt_file?.map((item) => (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => handleDownload(item)}
                      >
                        <Download className="w-4 h-4" />
                        <span>
                          {type === "payment"
                            ? "Payment Advice"
                            : type === "vendor-consent" ||
                                type === "contact-consent"
                              ? item.split("/").pop()
                              : "Default"}
                        </span>
                      </Badge>
                    ))}

                  {/* New Uploads */}
                  {files.map((file, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1 pr-2"
                    >
                      <span
                        title={file.name}
                        onClick={() => handleDownload(file)}
                        className="cursor-pointer"
                      >
                        {file.name}
                      </span>
                      <X
                        className="w-4 h-4 ml-1 cursor-pointer"
                        onClick={() => handleRemoveFile(index)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit">Send Email</Button>
              <Button
                variant="secondary"
                className="cancel-btn ml-3"
                onClick={() => toggleCancel()}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SendEmailAttach;
